import {exists, sessionStorageGetParsedItem} from '@aboutyou/utils';
import React, {useEffect} from 'react';
import {getIsStorageConsentGiven} from '../utils/consentBannerAcceptedState';
import {
  EMPTY_VALUE,
  useStoredValue,
  useUpdateStoreValue,
} from './SesionStorageContext';

export function useSessionStorage<Value>({
  key,
  defaultValue,
  fallbackValue = defaultValue,
  forcePersistValue_ILLEGALLY = false,
  onInitialSyncFromStorage,
}: {
  key: string;
  /** Default value is used when no value exists in browser's session storage */
  defaultValue: Value;
  /** Fallback value is used before hydration is done and on the server */
  fallbackValue?: Value;
  /** Setting this to true will force save the value in sessionStorage even when there is no cookie consent for doing so.
   * This is in general a legal threat and can only be used if agreed with the legal department.
   * It will be mandatory to adjust the consent banner text for all countries before the change can go online.
   * Please use this only if it's strictly mandatory to make the ABOUT YOU website work
   */
  forcePersistValue_ILLEGALLY?: boolean;
  onInitialSyncFromStorage?: (
    nextValue: Value,
    initialValue: Value | undefined,
  ) => void;
}) {
  if (!process.browser) {
    return [
      fallbackValue,
      (_newValue: Value | undefined) => {
        throw new Error(
          `useSessionStorage update should not be used on the server. It was called for Key: ${key} with value ${value}`,
        );
      },
    ] as const;
  }

  /* eslint-disable react-hooks/rules-of-hooks */
  const storeValue = useStoredValue(key) as Value | undefined;
  const updateStoreValue = useUpdateStoreValue();

  const value: Value | undefined =
    storeValue === EMPTY_VALUE ? undefined : storeValue ?? defaultValue;

  const updateCallback = React.useCallback(
    (nextValue: Value | undefined) => {
      updateStoreValue({
        key,
        nextValue,
        forcePersistValue_ILLEGALLY,
      });
    },
    [forcePersistValue_ILLEGALLY, key, updateStoreValue],
  );

  useEffect(() => {
    if (!forcePersistValue_ILLEGALLY && !getIsStorageConsentGiven()) return; // do not read from localStorage if the user has not accepted the cookie banner

    const valueFromSessionStorage = sessionStorageGetParsedItem(key);

    if (exists(valueFromSessionStorage) && valueFromSessionStorage !== value) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- we want to update the state here once on mount to update the value from localStorage which is not available on the server
      updateCallback(valueFromSessionStorage);
      onInitialSyncFromStorage?.(valueFromSessionStorage, value);
    }
  }, []);

  return [value, updateCallback] as const;
}
